import type { SagaIterator } from 'redux-saga';
import { call, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { crashReporterInterface } from 'app-components/utils/core';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { cpMetaForResponseAction } from '../core/actionHelpers';
import type {
  QcsSyncResourcesConfigProps,
  ResourceActionCreator,
  ResourceFailureActionCreator,
} from '../core/resourceStoreTypes';
import {
  type CreditRegistrationMeta,
  clearCreditRegistrationFlow,
  getCreditRegistrationFlowId,
  getCreditRegistrationSsnDiagnostics,
  resolveCreditRegistrationSsn,
  sanitizeCreditRegistrationMeta,
} from '../credit-profile/registrationFlow';
import { getCreditProfile } from '../credit-profile/selectors';
import type { RegisterCreditProfilePayload } from '../credit-profile/types';
import * as actions from './actions';
import { parseCreditReport } from './creditReportSchema';
import { type CreditReportType, mkCreditReport } from './types';

const mkQcsSyncCreditReportConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<CreditReportType>();
const CREDIT_REPORT_RECEIVED_STATUS = 'CREDIT_REPORT_RECEIVED';

export const mkResourceConfig = (
  successAction: ResourceActionCreator<CreditReportType>,
  failureAction: ResourceFailureActionCreator,
  query = '',
  transformResourceWithResponse: QcsSyncResourcesConfigProps<CreditReportType>['transformResourceWithResponse'] = (
    resource,
    response,
  ) => mkCreditReport({ ...parseCreditReport(response.data), ...resource }),
) =>
  mkQcsSyncCreditReportConfig({
    resourceName: 'credit-report',
    resourceBaseUrl: `/creditscore/credit-report${query}`,
    successAction,
    failureAction,
    transformResponseToResource: (response) => mkCreditReport(parseCreditReport(response?.data)),
    transformResourceWithResponse,
  });

export function* handleGetCreditReport(action: ReturnType<typeof actions.getCreditReport>): SagaIterator {
  const idFromAction = action.payload?.creditProfileRegistrationId;
  const creditProfileFromState = yield select(getCreditProfile);
  const idFromState = creditProfileFromState?.id;
  const creditProfileRegistrationId = idFromAction || idFromState;

  if (!creditProfileRegistrationId) {
    return;
  }

  const config = mkResourceConfig(
    actions.getCreditReportSuccess,
    actions.getCreditReportFailure,
    `?creditProfileRegistrationId=${creditProfileRegistrationId}`,
  );
  yield call(resourceSagaUtils.qcsSyncGetResource<CreditReportType>, config);
}

export function* getCreditReportActionWatcher() {
  yield takeEvery(
    actions.getCreditReport.type,
    function* handle(action: ReturnType<typeof actions.getCreditReport>): SagaIterator {
      // A fetch that read before this device's update committed would put the old report back.
      yield race({
        fetched: call(handleGetCreditReport, action),
        updated: take(actions.getRefreshedCreditReportSuccess),
      });
    },
  );
}

// Response actions copy the request meta, so naming the requested registration there lets consumers ignore a
// response that arrives after the registration was replaced.
const withRequestedRegistration = (
  meta: CreditRegistrationMeta | undefined,
  payload: RegisterCreditProfilePayload | undefined,
): CreditRegistrationMeta | undefined => {
  const creditProfileRegistrationId = payload?.creditProfileRegistrationId ?? payload?.id;
  return creditProfileRegistrationId ? { ...meta, props: { ...meta?.props, creditProfileRegistrationId } } : meta;
};

// A refresh sends the registration with its SSN, and QCS answers with the report.
type RefreshRequest = ReturnType<typeof actions.refreshCreditReport>['payload'] & { ssn: string };

export function* refreshCreditReportActionWatcher() {
  const config = mkResourceConfig(
    actions.refreshCreditReportSuccess,
    actions.refreshCreditReportFailure,
    `/refresh`,
    (_resource, response) => mkCreditReport(parseCreditReport(response.data)),
  );
  yield takeEvery(
    actions.refreshCreditReport.type,
    function* handle(action: ReturnType<typeof actions.refreshCreditReport>): SagaIterator {
      const ssn = resolveCreditRegistrationSsn(action);
      if (!ssn) {
        const error = new Error(`Missing SSN for ${action.type}`);
        yield call(crashReporterInterface.reportError, error, {
          custom: {
            actionType: action.type,
            ...getCreditRegistrationSsnDiagnostics(action),
          },
        });
        yield put(actions.refreshCreditReportFailure(error, cpMetaForResponseAction(action.meta) ?? undefined));
        action.meta?.reject?.(error);
        return;
      }
      yield call(resourceSagaUtils.qcsSyncCreateResource<CreditReportType, RefreshRequest>, config, {
        ...action,
        payload: { ...action.payload, ssn },
        meta: withRequestedRegistration(sanitizeCreditRegistrationMeta(action.meta), action.payload),
      });
    },
  );
}

export function* refetchCreditReportActionWatcher() {
  const config = mkResourceConfig(
    actions.getRefreshedCreditReportSuccess,
    actions.getRefreshedCreditReportFailure,
    `/refresh`,
    (_resource, response) => mkCreditReport({ ...parseCreditReport(response.data), isRefreshed: true }),
  );
  yield takeEvery(
    actions.getRefreshedCreditReport.type,
    function* handle(action: ReturnType<typeof actions.getRefreshedCreditReport>): SagaIterator {
      yield call(
        resourceSagaUtils.qcsSyncCreateResource<CreditReportType, actions.RefreshedCreditReportPayload>,
        config,
        {
          ...action,
          meta: withRequestedRegistration(action.meta, action.payload),
        },
      );
    },
  );
}

export const clearCompletedCreditRegistrationFlow = (
  action: ReturnType<typeof actions.refreshCreditReportSuccess>,
): void => {
  if (action.payload.refreshStatus?.status === CREDIT_REPORT_RECEIVED_STATUS) {
    clearCreditRegistrationFlow(getCreditRegistrationFlowId(action));
  }
};

export function* clearCreditRegistrationFlowActionWatcher() {
  yield takeEvery(actions.refreshCreditReportSuccess, clearCompletedCreditRegistrationFlow);
}

export default [
  getCreditReportActionWatcher,
  refreshCreditReportActionWatcher,
  refetchCreditReportActionWatcher,
  clearCreditRegistrationFlowActionWatcher,
];
